import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { 
  CheckCircle2, 
  Circle,
  ChevronRight,
  ChevronDown,
  Flame,
  Trophy,
  Dumbbell,
  Zap,
  Eye,
  X,
  Clock,
  PlayCircle,
  RotateCcw
} from "lucide-react";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SemanaPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [cycleData, setCycleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [previewSession, setPreviewSession] = useState(null);
  const [sessionExercises, setSessionExercises] = useState({});
  const [loadingExercises, setLoadingExercises] = useState({});

  useEffect(() => {
    fetchCycleData();
  }, []);

  const fetchCycleData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workout/week`, {
        headers: getAuthHeaders(),
      });
      setCycleData(response.data.week_data);
    } catch (error) {
      console.error("Error fetching cycle data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionExercises = async (dayCode) => {
    if (sessionExercises[dayCode] || loadingExercises[dayCode]) return;
    
    setLoadingExercises(prev => ({ ...prev, [dayCode]: true }));
    try {
      const response = await axios.get(`${API_URL}/api/workout/day/${dayCode}`, {
        headers: getAuthHeaders(),
      });
      setSessionExercises(prev => ({
        ...prev,
        [dayCode]: response.data.workout?.exercises || []
      }));
    } catch (error) {
      console.error("Error fetching session exercises:", error);
    } finally {
      setLoadingExercises(prev => ({ ...prev, [dayCode]: false }));
    }
  };

  const handleSessionClick = (session) => {
    if (session.is_rest_day) return;
    
    if (session.is_current) {
      navigate("/hoy");
    } else {
      // Show preview modal for other sessions
      setPreviewSession(session);
      if (session.code) {
        fetchSessionExercises(session.code);
      }
    }
  };

  const toggleExpand = (session, e) => {
    e.stopPropagation();
    if (session.is_rest_day) return;
    
    const sessionKey = session.code;
    if (expandedSession === sessionKey) {
      setExpandedSession(null);
    } else {
      setExpandedSession(sessionKey);
      if (session.code) {
        fetchSessionExercises(session.code);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#666] mt-4">Cargando ciclo...</p>
      </div>
    );
  }

  if (!cycleData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <RotateCcw className="w-16 h-16 text-[#333] mb-4" />
        <h2 className="text-xl font-heading font-bold text-white mb-2">Sin programa</h2>
        <p className="text-[#666]">Asigna un programa para ver tu ciclo de entrenamiento</p>
      </div>
    );
  }

  // Filter out rest days and get only training sessions
  const trainingSessions = cycleData.days.filter(d => !d.is_rest_day);
  const completedCount = trainingSessions.filter(d => d.is_completed).length;
  const totalSessions = trainingSessions.length;
  const progressPercentage = totalSessions > 0 ? (completedCount / totalSessions) * 100 : 0;
  
  // Find the current (next) session
  const currentSessionIndex = trainingSessions.findIndex(d => d.is_today || (!d.is_completed && !d.is_today));
  
  // Map training sessions to flexible session numbers
  const sessionsWithNumbers = trainingSessions.map((session, index) => ({
    ...session,
    sessionNumber: index + 1,
    is_current: session.is_today || (index === currentSessionIndex && !session.is_completed),
    is_pending: !session.is_completed && index > currentSessionIndex
  }));

  // Preview modal for non-current sessions
  const PreviewModal = () => {
    if (!previewSession) return null;
    
    const exercises = sessionExercises[previewSession.code] || [];
    const isLoading = loadingExercises[previewSession.code];
    const isPastCompleted = previewSession.is_completed;
    
    const modalContent = (
      <div 
        className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80"
        onClick={() => setPreviewSession(null)}
      >
        <div 
          className="w-full max-w-lg bg-[#121212] rounded-t-3xl border-t border-[#2A2A2A] max-h-[85vh] flex flex-col animate-slide-up"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle bar */}
          <div className="w-12 h-1 bg-[#3A3A3A] rounded-full mx-auto mt-4 mb-2 flex-shrink-0" />
          
          {/* Header */}
          <div className="p-4 border-b border-[#2A2A2A] flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-14 h-14 rounded-2xl flex items-center justify-center",
                  isPastCompleted 
                    ? "bg-[#22C55E]/20"
                    : "bg-[#D4AF37]/20"
                )}>
                  {isPastCompleted ? (
                    <CheckCircle2 className="w-7 h-7 text-[#22C55E]" />
                  ) : (
                    <span className="text-2xl font-heading font-black text-[#D4AF37]">
                      {previewSession.sessionNumber}
                    </span>
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold text-white">
                    {previewSession.name}
                  </h2>
                  <p className="text-[#888] text-sm flex items-center gap-2">
                    <span>Sesión {previewSession.sessionNumber}</span>
                    <span>•</span>
                    <span>{previewSession.exercise_count} ejercicios</span>
                    {isPastCompleted && (
                      <>
                        <span>•</span>
                        <span className="text-[#22C55E]">Completado</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setPreviewSession(null)}
                className="p-2 hover:bg-[#2A2A2A] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#888]" />
              </button>
            </div>
          </div>
          
          {/* Status banner */}
          <div className={cn(
            "px-4 py-3 flex items-center gap-3 flex-shrink-0",
            isPastCompleted 
              ? "bg-[#22C55E]/10"
              : "bg-[#D4AF37]/10"
          )}>
            {isPastCompleted ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                <span className="text-[#22C55E] text-sm font-medium">
                  Ya completaste esta sesión
                </span>
              </>
            ) : (
              <>
                <Eye className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-medium">
                  Vista previa del entrenamiento
                </span>
              </>
            )}
          </div>
          
          {/* Exercise list - Native scrollable */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : exercises.length > 0 ? (
              <div className="space-y-2 pb-4">
                {exercises.map((ex, idx) => (
                  <div 
                    key={ex.id || idx}
                    className={cn(
                      "p-3 rounded-xl border",
                      isPastCompleted
                        ? "bg-[#22C55E]/5 border-[#22C55E]/20"
                        : "bg-[#1A1A1A] border-[#2A2A2A]"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold",
                        isPastCompleted
                          ? "bg-[#22C55E]/20 text-[#22C55E]"
                          : "bg-[#2A2A2A] text-[#888]"
                      )}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "font-medium truncate",
                          isPastCompleted ? "text-[#22C55E]" : "text-white"
                        )}>
                          {ex.name}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-[#888]">
                          <span className={isPastCompleted ? "text-[#22C55E]/70" : "text-[#D4AF37]"}>
                            {ex.sets} × {ex.reps}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ex.rest_seconds}s
                          </span>
                        </div>
                      </div>
                      {ex.superset_group && (
                        <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold rounded">
                          SS
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#666] py-8">No hay ejercicios</p>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-[#2A2A2A] flex-shrink-0">
            <button
              onClick={() => setPreviewSession(null)}
              className="w-full py-4 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white font-semibold rounded-xl transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
    
    return createPortal(modalContent, document.body);
  };

  return (
    <div className="pb-8 animate-fade-in" data-testid="semana-page">
      {/* Header - Ciclo de Entrenamiento */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-heading font-black text-white">
              Mi Ciclo
            </h1>
            <p className="text-[#666] text-sm">
              Entrena en orden, a tu ritmo
            </p>
          </div>
          <div className="text-right">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider">Progreso</p>
            <p className="text-2xl font-heading font-black text-white">
              {completedCount}/{totalSessions}
            </p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-3 bg-[#1A1A1A] rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-500",
              progressPercentage === 100 
                ? "bg-gradient-to-r from-[#22C55E] to-emerald-400"
                : "bg-gradient-to-r from-[#D4AF37] to-[#F59E0B]"
            )}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        {/* Motivational message */}
        <p className="text-center text-[#888] text-sm mt-3 italic">
          {progressPercentage === 100 
            ? "¡Ciclo completado! Eres imparable."
            : progressPercentage >= 60 
              ? "¡Vas muy bien! Sigue así."
              : progressPercentage > 0
                ? "Cada sesión cuenta. ¡A por la siguiente!"
                : "Nuevo ciclo, nuevas ganancias."}
        </p>
      </div>

      {/* Sessions list - Flexible progression */}
      <div className="space-y-3">
        {sessionsWithNumbers.map((session, index) => {
          const isExpanded = expandedSession === session.code;
          const exercises = sessionExercises[session.code] || [];
          const isLoadingEx = loadingExercises[session.code];
          
          return (
            <Card
              key={session.code || `session-${index}`}
              className={cn(
                "border transition-all duration-300 overflow-hidden",
                session.is_current
                  ? "bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 border-[#D4AF37]/50 ring-2 ring-[#D4AF37]/30"
                  : session.is_completed
                    ? "bg-[#22C55E]/10 border-[#22C55E]/40"
                    : "bg-[#121212] border-[#2A2A2A]"
              )}
              data-testid={`session-card-${session.sessionNumber}`}
            >
              <CardContent className="p-0">
                {/* Main row - clickable */}
                <div
                  onClick={() => handleSessionClick({ ...session, sessionNumber: session.sessionNumber })}
                  className="w-full flex items-center active:scale-[0.99] transition-transform cursor-pointer"
                >
                  {/* Session number */}
                  <div className={cn(
                    "w-16 sm:w-20 py-4 flex flex-col items-center justify-center border-r flex-shrink-0",
                    session.is_current
                      ? "border-[#D4AF37]/30 bg-[#D4AF37]/10"
                      : session.is_completed
                        ? "border-[#22C55E]/30 bg-[#22C55E]/10"
                        : "border-[#2A2A2A]"
                  )}>
                    <span className={cn(
                      "text-xs uppercase font-semibold",
                      session.is_current ? "text-[#D4AF37]" : session.is_completed ? "text-[#22C55E]" : "text-[#666]"
                    )}>
                      Sesión
                    </span>
                    <span className={cn(
                      "text-2xl font-heading font-black mt-0.5",
                      session.is_current 
                        ? "text-[#D4AF37]" 
                        : session.is_completed 
                          ? "text-[#22C55E]"
                          : "text-white"
                    )}>
                      {session.sessionNumber}
                    </span>
                  </div>
                  
                  {/* Workout info */}
                  <div className="flex-1 py-3 px-4 min-w-0 text-left">
                    <div className="flex items-center gap-2">
                      <h3 className={cn(
                        "font-heading font-bold truncate",
                        session.is_current 
                          ? "text-[#D4AF37]" 
                          : session.is_completed 
                            ? "text-[#22C55E]"
                            : "text-white"
                      )}>
                        {session.name}
                      </h3>
                      {session.is_current && (
                        <span className="px-2 py-0.5 bg-[#D4AF37] text-[#0B0B0B] text-[10px] font-bold rounded-full uppercase flex-shrink-0">
                          Siguiente
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-xs text-[#888]">
                        <Dumbbell className="w-3 h-3" />
                        {session.exercise_count} ejercicios
                      </span>
                      {session.is_completed && (
                        <span className="flex items-center gap-1 text-xs text-[#22C55E]">
                          <CheckCircle2 className="w-3 h-3" />
                          Completado
                        </span>
                      )}
                      {session.is_pending && (
                        <span className="flex items-center gap-1 text-xs text-[#555]">
                          <Circle className="w-3 h-3" />
                          Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Status indicator */}
                  <div className="pr-4 flex-shrink-0 flex items-center gap-2">
                    {/* Expand button */}
                    <button
                      onClick={(e) => toggleExpand(session, e)}
                      className={cn(
                        "p-2 rounded-lg transition-colors",
                        isExpanded ? "bg-[#2A2A2A]" : "hover:bg-[#1A1A1A]"
                      )}
                    >
                      <ChevronDown className={cn(
                        "w-4 h-4 text-[#666] transition-transform",
                        isExpanded && "rotate-180"
                      )} />
                    </button>
                    
                    {/* Status icon */}
                    {session.is_completed ? (
                      <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-[#22C55E]" />
                      </div>
                    ) : session.is_current ? (
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <PlayCircle className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                        <Circle className="w-5 h-5 text-[#444]" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Expanded exercise list */}
                {isExpanded && (
                  <div className="border-t border-[#2A2A2A] bg-[#0B0B0B]/50 p-3">
                    {isLoadingEx ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : exercises.length > 0 ? (
                      <div className="space-y-2">
                        {exercises.map((ex, idx) => (
                          <div 
                            key={ex.id || idx}
                            className="flex items-center gap-3 p-2 rounded-lg bg-[#1A1A1A]/50"
                          >
                            <span className={cn(
                              "w-6 h-6 rounded text-xs font-bold flex items-center justify-center",
                              session.is_completed
                                ? "bg-[#22C55E]/20 text-[#22C55E]"
                                : "bg-[#2A2A2A] text-[#666]"
                            )}>
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{ex.name}</p>
                              <p className="text-xs text-[#666]">{ex.sets}×{ex.reps}</p>
                            </div>
                            {ex.superset_group && (
                              <span className="px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-[9px] font-bold rounded">
                                SS
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-[#666] text-sm py-2">Sin ejercicios</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Bottom stats */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-3 text-center">
          <div className="w-8 h-8 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto mb-2">
            <CheckCircle2 className="w-4 h-4 text-[#22C55E]" />
          </div>
          <p className="text-lg font-heading font-bold text-white">{completedCount}</p>
          <p className="text-[10px] text-[#666] uppercase">Completadas</p>
        </div>
        
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-3 text-center">
          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-2">
            <Circle className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <p className="text-lg font-heading font-bold text-white">
            {totalSessions - completedCount}
          </p>
          <p className="text-[10px] text-[#666] uppercase">Pendientes</p>
        </div>
        
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-3 text-center">
          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-2">
            <Flame className="w-4 h-4 text-orange-500" />
          </div>
          <p className="text-lg font-heading font-bold text-white">
            {Math.round(progressPercentage)}%
          </p>
          <p className="text-[10px] text-[#666] uppercase">Progreso</p>
        </div>
      </div>
      
      {/* Info banner */}
      <div className="mt-6 p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
        <p className="text-[#888] text-sm text-center">
          <span className="text-[#D4AF37] font-semibold">Entrena cuando quieras.</span> Solo sigue el orden de las sesiones para progresar correctamente.
        </p>
      </div>

      {/* Preview Modal */}
      <PreviewModal />
    </div>
  );
};

export default SemanaPage;
